import { useEffect, useState } from 'react';
import { X, Loader2, CheckCircle2, Copy, Check, Pencil, Trash2 } from 'lucide-react';
import { SECTOR_OPTIONS, EMPLOYEE_TIER_OPTIONS, STAGE_LABELS, LOST_REASON_OPTIONS, SWISS_CANTONS, SOURCE_OPTIONS } from '../config/leadTaxonomy';
import type { Lead, LeadActivity, LostReason } from '../types/lead.types';
import { subscribeToActivities, addActivity, setStage, getNextStage, convertToMandate, deleteLead } from '../services/leadService';
import { formatDate } from '../utils/date';
import { TaskList } from './TaskList';

interface Props {
  lead: Lead;
  currentUid: string;
  /** null se non team Arcanum — 'arcanum_admin' abilita l'eliminazione (v. firestore.rules). */
  arcanumLevel: string | null;
  /** Vero quando il pannello si apre da un drop sulla colonna "Perso" —
   * apre subito i motivi invece di richiedere un secondo click su "Segna
   * Perso" (v. PipelinePage.tsx, handleDropOnLost). */
  autoOpenLostReason?: boolean;
  onClose: () => void;
  onEdit: (lead: Lead) => void;
}

export function LeadDetailPanel({ lead, currentUid, arcanumLevel, autoOpenLostReason, onClose, onEdit }: Props) {
  const [activities, setActivities] = useState<LeadActivity[]>([]);
  const [note, setNote] = useState('');
  const [converting, setConverting] = useState(false);
  const [convertError, setConvertError] = useState('');
  const [onboardingUrl, setOnboardingUrl] = useState<string | null>(
    lead.onboardingToken ? buildOnboardingUrl(lead.onboardingToken) : null
  );
  const [copied, setCopied] = useState(false);
  const [showLostReason, setShowLostReason] = useState(!!autoOpenLostReason);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => subscribeToActivities(lead.id, setActivities), [lead.id]);

  const sectorLabel = SECTOR_OPTIONS.find((s) => s.id === lead.sector)?.label ?? lead.sector;
  const tierLabel = lead.employeeTier ? (EMPLOYEE_TIER_OPTIONS.find((t) => t.id === lead.employeeTier)?.label ?? lead.employeeTier) : 'Da confermare';
  const sourceLabel = SOURCE_OPTIONS.find((s) => s.id === lead.source)?.label ?? lead.source;
  const cantonLabel = lead.canton
    ? (SWISS_CANTONS.find((c) => c.id === lead.canton)?.name ?? lead.canton) + ` (${lead.canton})`
    : '—';
  const nextStage = getNextStage(lead.stage);
  const isTerminal = lead.stage === 'won' || lead.stage === 'lost';

  const handleAddNote = async () => {
    if (!note.trim()) return;
    await addActivity(lead.id, 'note', note.trim(), currentUid);
    setNote('');
  };

  const handleAdvance = async () => {
    if (!nextStage) return;
    await setStage(lead.id, nextStage);
  };

  const handleLost = async (reason: LostReason) => {
    await setStage(lead.id, 'lost', reason);
    setShowLostReason(false);
  };

  const handleConvert = async () => {
    setConverting(true);
    setConvertError('');
    try {
      const { onboardingToken } = await convertToMandate(lead.id);
      setOnboardingUrl(buildOnboardingUrl(onboardingToken));
    } catch (err) {
      setConvertError(err instanceof Error ? err.message : 'Conversione fallita.');
    } finally {
      setConverting(false);
    }
  };

  const copyLink = () => {
    if (!onboardingUrl) return;
    navigator.clipboard.writeText(onboardingUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleDelete = async () => {
    if (!confirmingDelete) {
      setConfirmingDelete(true);
      return;
    }
    setDeleting(true);
    try {
      await deleteLead(lead.id);
      onClose();
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-md h-full bg-white shadow-2xl overflow-y-auto p-6 space-y-4">
        <div className="absolute top-4 right-4 flex items-center gap-1">
          <button onClick={() => onEdit(lead)} title="Modifica lead" className="p-1.5 text-gray-400 hover:text-navy-900">
            <Pencil size={16} />
          </button>
          {arcanumLevel === 'arcanum_admin' && (
            <button
              onClick={handleDelete}
              disabled={deleting}
              title="Elimina lead"
              className={confirmingDelete ? 'px-2 py-1 text-xs font-semibold rounded bg-red-600 text-white' : 'p-1.5 text-gray-400 hover:text-red-600'}
            >
              {confirmingDelete ? (deleting ? '…' : 'Conferma') : <Trash2 size={16} />}
            </button>
          )}
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <div>
          <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded bg-navy-900 text-white">
            {STAGE_LABELS[lead.stage]}
          </span>
          <h2 className="text-lg font-bold text-navy-900 mt-2">{lead.organizationName}</h2>
          {lead.contactName ? (
            <p className="text-sm text-gray-500">
              {lead.contactName} — {lead.contactRole || 'ruolo n/d'}
              {lead.contactSource === 'ai_web_search' && (
                <span className="ml-1.5 text-[10px] uppercase font-semibold text-indigo-500">
                  Trovato dall'AI — verificare prima di contattare
                </span>
              )}
            </p>
          ) : (
            <p className="text-sm font-medium text-amber-600">⚠ Contatto da individuare</p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-2 text-sm">
          <Field label="Settore" value={sectorLabel} />
          <Field label="Dimensione" value={tierLabel} />
          <Field label="Email" value={lead.email || '—'} />
          <Field label="Telefono" value={lead.phone || '—'} />
          <Field label="Indirizzo" value={lead.streetAddress || '—'} />
          <Field label="Località" value={lead.city ? `${lead.postalCode ? lead.postalCode + ' ' : ''}${lead.city}` : '—'} />
          <Field label="Cantone" value={cantonLabel} />
          <div>
            <p className="text-[10px] uppercase text-gray-400">Sito web</p>
            {lead.website ? (
              <a href={lead.website} target="_blank" rel="noreferrer" className="text-swiss-red hover:underline truncate block">
                {lead.website.replace(/^https?:\/\//, '')}
              </a>
            ) : (
              <p className="text-gray-700">—</p>
            )}
          </div>
          <Field label="Fonte" value={sourceLabel} />
          <Field label="Prossimo follow-up" value={formatDate(lead.nextFollowUpAt) || '—'} />
          <Field label="Valore stimato" value={lead.estimatedDealValue != null ? `CHF ${lead.estimatedDealValue.toLocaleString('it-CH')}` : '—'} />
        </div>

        {lead.painPoint && (
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase">Motivo del contatto</p>
            <p className="text-sm text-gray-700">{lead.painPoint}</p>
          </div>
        )}

        {lead.registryPurpose && (
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase">Scopo sociale dichiarato</p>
            <p className="text-sm text-gray-700">{lead.registryPurpose}</p>
          </div>
        )}

        {/* Valutazione AI (Gemini) — Fase 3, v. leadProfilingClassifier.ts.
            Assente finché non è ancora arrivata (di norma pochi secondi
            dopo la creazione del lead da parte dell'agente). */}
        {lead.dataProfilingLikely != null && (
          <div className={`p-3 rounded-lg border text-sm ${lead.dataProfilingLikely ? 'bg-indigo-50 border-indigo-200 text-indigo-800' : 'bg-gray-50 border-gray-200 text-gray-600'}`}>
            <p className="text-xs font-semibold uppercase mb-1">
              Valutazione AI — {lead.dataProfilingLikely ? 'Probabile profilazione dati' : 'Profilazione non probabile'}
              {lead.dataProfilingConfidence != null && ` (${Math.round(lead.dataProfilingConfidence * 100)}%)`}
            </p>
            {lead.dataProfilingReasoning && <p>{lead.dataProfilingReasoning}</p>}
          </div>
        )}

        {/* Azioni pipeline */}
        {!isTerminal && (
          <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
            {nextStage && (
              <button onClick={handleAdvance} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-navy-900 text-white">
                Avanza a "{STAGE_LABELS[nextStage]}"
              </button>
            )}
            <button
              onClick={handleConvert}
              disabled={converting}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-green-600 text-white disabled:opacity-50 flex items-center gap-1"
            >
              {converting ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
              Segna Vinto
            </button>
            <button
              onClick={() => setShowLostReason((v) => !v)}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-red-300 text-red-600"
            >
              Segna Perso
            </button>
          </div>
        )}

        {showLostReason && (
          <div className="flex flex-wrap gap-1.5">
            {LOST_REASON_OPTIONS.map((r) => (
              <button
                key={r.id}
                onClick={() => handleLost(r.id)}
                className="px-2 py-1 rounded text-xs border border-gray-300 hover:bg-gray-50"
              >
                {r.label}
              </button>
            ))}
          </div>
        )}

        {convertError && <p className="text-sm text-red-600">{convertError}</p>}

        {onboardingUrl && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg space-y-2">
            <p className="text-xs text-green-800">Mandato generato — invia questo link al cliente per completare l'onboarding:</p>
            <div className="flex items-center gap-2">
              <input readOnly value={onboardingUrl} onFocus={(e) => e.target.select()} className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded" />
              <button onClick={copyLink} className="p-1.5 rounded bg-navy-900 text-white shrink-0">
                {copied ? <Check size={12} /> : <Copy size={12} />}
              </button>
            </div>
          </div>
        )}

        {lead.stage === 'lost' && lead.lostReason && (
          <p className="text-sm text-red-600">Perso — {LOST_REASON_OPTIONS.find((r) => r.id === lead.lostReason)?.label}</p>
        )}

        {/* Task */}
        <div className="pt-2 border-t border-gray-100">
          <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Task</p>
          <TaskList leadId={lead.id} createdBy={currentUid} />
        </div>

        {/* Attività */}
        <div className="pt-2 border-t border-gray-100">
          <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Note e attività</p>
          <div className="flex gap-2 mb-3">
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddNote()}
              placeholder="Aggiungi una nota…"
              className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
            />
            <button onClick={handleAddNote} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-100">
              Aggiungi
            </button>
          </div>
          <ul className="space-y-2">
            {activities.map((a) => (
              <li key={a.id} className="text-sm text-gray-700 bg-gray-50 rounded-lg p-2">
                {a.text}
              </li>
            ))}
            {activities.length === 0 && <p className="text-xs text-gray-400">Nessuna attività registrata.</p>}
          </ul>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[10px] uppercase text-gray-400">{label}</p>
      <p className="text-gray-700">{value}</p>
    </div>
  );
}

// Origine dell'app piattaforma: da impostare a dominio di produzione reale
// (VITE_PLATFORM_ORIGIN) — vedi crm/.env.example. Fallback al dominio
// custom reale (arcanum2.ch, collegato il 5/10/2026) se la env var manca.
function buildOnboardingUrl(token: string): string {
  const origin = import.meta.env.VITE_PLATFORM_ORIGIN || 'https://arcanum2.ch';
  return `${origin}/external-onboarding/${token}`;
}
